import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, Save, AlertCircle, Info, ChevronDown, ChevronUp } from 'lucide-react';
import EvaluationTooltip from '../components/EvaluationTooltip';
import CertificationNav from '../components/CertificationNav';
import axios from 'axios';
import { useTranslation } from '../translations';

/**
 * EvaluationForm - Formulário de Avaliação Técnica
 * 
 * VERSÃO 2 - Alinhado com Estratégia Consolidada
 * 
 * 14 critérios distribuídos em 4 grupos:
 * - Grupo A: Técnica de Sangria (45%)
 * - Grupo B1: Preparação de Safra (20%)
 * - Grupo B2: Manutenção Diária (15%)
 * - Grupo C: Aplicações Químicas (20%)
 * 
 * Tripé:
 * - Justiça: Pesos validados com usuário
 * - Segurança: Campos opcionais para LGPD
 * - Simplicidade: Interface visual e intuitiva
 */

// Feature flag para habilitar/desabilitar tooltips
const ENABLE_TOOLTIPS = true;

// Versão do formulário (para tracking)
const FORM_VERSION = "2.0";

const API = process.env.REACT_APP_BACKEND_URL;

const QUALIFICATION_LABELS = {
  engenheiro_agronomo: { label: "Engenheiro Agronomo", weight: 3 },
  tecnico_agricola: { label: "Tecnico Agricola", weight: 3 },
  lider_equipe: { label: "Lider de Equipe", weight: 1 },
  proprietario: { label: "Proprietario", weight: 1 },
  outro: { label: "Outro", weight: 1 },
};

const EvaluationForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Estado do avaliador
  const [evaluatorInfo, setEvaluatorInfo] = useState(null);
  const [showQualEdit, setShowQualEdit] = useState(false);
  const [newQual, setNewQual] = useState('');
  const [qualSaving, setQualSaving] = useState(false);
  
  const QUALIFICATION_LABELS_T = {
    engenheiro_agronomo: { label: t('evalRoleEngineer'), weight: 3 },
    tecnico_agricola: { label: t('evalRoleTechnician'), weight: 3 },
    lider_equipe: { label: t('evalRoleLeader'), weight: 1 },
    proprietario: { label: t('evalRoleOwner'), weight: 1 },
    outro: { label: t('evalRoleOther'), weight: 1 },
  };
  // Estado do formulário (must be declared before useEffects that reference them)
  const [workerId, setWorkerId] = useState('');
  const [tappingSystem, setTappingSystem] = useState(''); // Informativo, não pontuado
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [propertyHasContext, setPropertyHasContext] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(false);
  
  // Estado dos critérios - 14 itens com valores iniciais no meio da escala
  const [criteria, setCriteria] = useState({
    // Grupo A: Técnica de Sangria (45 pts)
    angle_cutting: 8,
    cutting_depth: 8,
    bark_consumption: 4,
    generatrix_marking: 4,
    // Grupo B1: Preparação de Safra (20 pts)
    aptitude_evaluation: 3,
    flagging: 3,
    equipment_installation: 3,
    panel_opening: 3,
    // Grupo B2: Manutenção Diária (15 pts)
    injury_prevention: 4,
    tapping_recovery: 3,
    gutter_cleaning: 1,
    // Grupo C: Aplicações Químicas (20 pts)
    ethrel_application: 4,
    fungicide_application: 4,
    coagulation_control: 3
  });
  
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Estado para expandir/colapsar grupos
  const [expandedGroups, setExpandedGroups] = useState({
    'A': true,
    'B1': true,
    'B2': true,
    'C': true
  });

  useEffect(() => {
    const fetchEvaluator = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/api/me`, { headers });
        const user = res.data;
        const qual = user.qualificacao_tecnica || 'proprietario';
        setEvaluatorInfo({
          name: user.name,
          qualificacao_tecnica: qual,
          weight: QUALIFICATION_LABELS[qual]?.weight || 1,
          label: QUALIFICATION_LABELS_T[qual]?.label || qual
        });
        setNewQual(qual);
      } catch (err) {
        console.error('Erro ao buscar dados do avaliador:', err);
      }
    };
    fetchEvaluator();
  }, []);

  // Carregar propriedades do avaliador
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoadingProperties(true);
        const token = localStorage.getItem('token');
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/api/bleeding-properties`, { headers });
        setProperties(res.data || []);
      } catch (err) {
        console.error('Erro ao buscar propriedades:', err);
      } finally {
        setLoadingProperties(false);
      }
    };
    fetchProperties();
  }, []);

  // Verificar se propriedade selecionada tem contexto de produção
  useEffect(() => {
    const checkContext = async () => {
      if (!selectedProperty) {
        setPropertyHasContext(false);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API}/api/contextual-performance/contexts/${selectedProperty}`, { headers });
        setPropertyHasContext(!!res.data);
      } catch {
        setPropertyHasContext(false);
      }
    };
    checkContext();
  }, [selectedProperty]);
  
  const handleQualUpdate = async () => {
    setQualSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.patch(`${API}/api/me/qualification`, { qualificacao_tecnica: newQual }, { headers });
      const qual = res.data.qualificacao_tecnica;
      setEvaluatorInfo(prev => ({
        ...prev,
        qualificacao_tecnica: qual,
        weight: res.data.evaluation_weight,
        label: QUALIFICATION_LABELS_T[qual]?.label || qual
      }));
      setShowQualEdit(false);
    } catch (err) {
      console.error('Erro ao atualizar qualificação:', err);
    } finally {
      setQualSaving(false);
    }
  };
  // Definição dos grupos de critérios - ALINHADO COM ESTRATÉGIA
  const criteriaGroups = {
    'A': {
      title: 'Técnica de Sangria',
      subtitle: '45 pontos',
      color: 'blue',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      items: [
        { key: 'angle_cutting', label: 'Ângulo de Corte', max: 15, step: 1, unit: 'pts', 
          hint: '33° descendente / 45° ascendente' },
        { key: 'cutting_depth', label: 'Profundidade de Corte', max: 15, step: 1, unit: 'pts',
          hint: '1,0-1,5mm do câmbio' },
        { key: 'bark_consumption', label: 'Consumo de Casca', max: 7.5, step: 0.5, unit: 'pts',
          hint: '1,2mm por sangria' },
        { key: 'generatrix_marking', label: 'Geratriz e Demarcação', max: 7.5, step: 0.5, unit: 'pts',
          hint: 'Linha vertical reta' }
      ]
    },
    'B1': {
      title: 'Preparação de Safra',
      subtitle: '20 pontos',
      color: 'green',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      items: [
        { key: 'aptitude_evaluation', label: 'Avaliação de Aptidão', max: 5, step: 1, unit: 'pts',
          hint: 'Árvores aptas vs não aptas' },
        { key: 'flagging', label: 'Bandeiramento', max: 5, step: 1, unit: 'pts',
          hint: 'Gabarito para consumo anual' },
        { key: 'equipment_installation', label: 'Instalação de Equipamentos', max: 5, step: 1, unit: 'pts',
          hint: 'Bica, caneca e arame' },
        { key: 'panel_opening', label: 'Abertura de Painel', max: 5, step: 1, unit: 'pts',
          hint: 'Abertura inicial e espelhamento' }
      ]
    },
    'B2': {
      title: 'Manutenção Diária',
      subtitle: '15 pontos',
      color: 'amber',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      textColor: 'text-amber-800',
      items: [
        { key: 'injury_prevention', label: 'Prevenção de Ferimentos', max: 7, step: 1, unit: 'pts',
          hint: 'Evitar atingir o lenho' },
        { key: 'tapping_recovery', label: 'Recuperação de Sangria', max: 6, step: 1, unit: 'pts',
          hint: 'Regularidade nas sangrias' },
        { key: 'gutter_cleaning', label: 'Limpeza de Bica', max: 2, step: 1, unit: 'pts',
          hint: 'Manter sem obstruções' }
      ]
    },
    'C': {
      title: 'Aplicações Químicas',
      subtitle: '20 pontos',
      color: 'purple',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-800',
      items: [
        { key: 'ethrel_application', label: 'Aplicação de Ethrel', max: 7, step: 1, unit: 'pts',
          hint: '2,5-3,3%, intervalo 30-45 dias' },
        { key: 'fungicide_application', label: t('evFungicideApplication'), max: 7, step: 1, unit: 'pts',
          hint: 'Preventivo regular' },
        { key: 'coagulation_control', label: 'Controle de Coagulação', max: 6, step: 1, unit: 'pts',
          hint: 'Látex líquido até coleta' }
      ]
    }
  };

  // Calcular pontuação por grupo
  const calculateGroupScore = (groupKey) => {
    const group = criteriaGroups[groupKey];
    return group.items.reduce((sum, item) => sum + (criteria[item.key] || 0), 0);
  };

  // Calcular pontuação total
  const calculateTotalScore = () => {
    return Object.keys(criteriaGroups).reduce((sum, groupKey) => {
      return sum + calculateGroupScore(groupKey);
    }, 0);
  };

  // Determinar selo baseado na pontuação
  const determineSeal = (score) => {
    if (score >= 90) return { type: 'gold', emoji: '🥇', label: 'Ouro - Sangrador Expert', color: 'text-yellow-600' };
    if (score >= 75) return { type: 'silver', emoji: '🥈', label: 'Prata - Sangrador Profissional', color: 'text-gray-500' };
    if (score >= 60) return { type: 'bronze', emoji: '🥉', label: 'Bronze - Sangrador Certificado', color: 'text-amber-700' };
    return { type: 'none', emoji: '👤', label: 'Em Desenvolvimento', color: 'text-gray-400' };
  };

  const totalScore = calculateTotalScore();
  const seal = determineSeal(totalScore);

  // Handler para mudança de valor de critério
  const handleCriteriaChange = (key, value, max) => {
    // Garantir que o valor está dentro dos limites
    const numValue = parseFloat(value);
    const clampedValue = Math.min(Math.max(0, numValue), max);
    
    setCriteria(prev => ({
      ...prev,
      [key]: clampedValue
    }));
  };

  // Toggle para expandir/colapsar grupo
  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Submit do formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!workerId.trim()) {
      setError(t('evWorkerIdRequired'));
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Preparar dados para envio
      const evaluationData = {
        worker_id: workerId.trim(),
        farm_id: selectedProperty || null,
        criteria: {
          ...criteria,
          tapping_system: tappingSystem || null // Campo informativo
        },
        notes: notes.trim(),
        form_version: FORM_VERSION
      };
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('evLoginRequired') || 'Você precisa estar logado para avaliar');
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${backendUrl}/api/evaluations/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(evaluationData)
      });
      
      if (response.ok) {
        const result = await response.json();
        setSuccess(true);
        
        // Redirecionar após sucesso
        setTimeout(() => {
          navigate(`/worker-dashboard/${workerId}`);
        }, 2000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || t('evCreateError'));
      }
    } catch (err) {
      console.error('Erro ao criar avaliação:', err);
      setError(t('evConnectionError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CertificationNav />
      <div className="min-h-screen bg-gray-100 py-6">
        <div className="container mx-auto px-4 max-w-4xl">
        
          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Award className="w-7 h-7 text-blue-600" />
                  Avaliacao Tecnica
                </h1>
                <p className="text-gray-600 mt-1">
                  Sistema de Certificacao HeveaTalent - 14 criterios
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">{t('evalFormVersion')} {FORM_VERSION}</p>
            </div>
          </div>
            
            {/* Banner do peso do avaliador */}
            {evaluatorInfo && (
              <div className={`mt-4 p-4 rounded-lg border ${
                evaluatorInfo.weight === 3 
                  ? 'bg-blue-50 border-blue-200' 
                  : 'bg-amber-50 border-amber-200'
              }`} data-testid="evaluator-weight-banner">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <span className={`text-2xl font-bold px-3 py-1 rounded-lg ${
                      evaluatorInfo.weight === 3 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {evaluatorInfo.weight}x
                    </span>
                    <div>
                      <p className="font-medium text-gray-800">
                        Sua avaliacao tera <strong>peso {evaluatorInfo.weight}x</strong>
                      </p>
                      <p className="text-sm text-gray-600">
                        Qualificacao: {evaluatorInfo.label}
                        {evaluatorInfo.weight === 1 && (
                          <span className="text-xs text-gray-400 ml-1">
                            (Engenheiros e Tecnicos recebem peso 3x)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowQualEdit(!showQualEdit)}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                    data-testid="edit-qualification-btn"
                  >
                    {showQualEdit ? t('cancel') : t('evChange')}
                  </button>
                </div>
                
                {/* Edição de qualificação inline */}
                {showQualEdit && (
                  <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-3" data-testid="qualification-edit-form">
                    <select
                      value={newQual}
                      onChange={(e) => setNewQual(e.target.value)}
                      className="flex-1 p-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="engenheiro_agronomo">{t('evAgronomistWeight')}</option>
                      <option value="tecnico_agricola">{t('evTechnicianWeight')}</option>
                      <option value="lider_equipe">{t('evTeamLeaderWeight')}</option>
                      <option value="proprietario">{t('evOwnerWeight')}</option>
                      <option value="outro">{t('evOtherWeight')}</option>
                    </select>
                    <button
                      type="button"
                      onClick={handleQualUpdate}
                      disabled={qualSaving || newQual === evaluatorInfo.qualificacao_tecnica}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:bg-gray-400"
                      data-testid="save-qualification-btn"
                    >
                      {qualSaving ? t('savingLabel') : t('save')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

        {/* Alertas */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">{t('evalError')}</p>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 font-semibold">✅ Avaliação criada com sucesso!</p>
            <p className="text-green-700 text-sm mt-1">{t('evalRedirecting')}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          
          {/* Identificação */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">📋 Identificação</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block font-medium text-gray-700 mb-2">
                  ID do Trabalhador *
                </label>
                <input
                  type="text"
                  value={workerId}
                  onChange={(e) => setWorkerId(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: worker_123"
                  required
                />
              </div>
              
              <div>
                <label className="block font-medium text-gray-700 mb-2">
                  Sistema de Sangria (informativo)
                </label>
                <select
                  value={tappingSystem}
                  onChange={(e) => setTappingSystem(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{t('evalSelectOption')}</option>
                  <option value="d/3">d/3 - A cada 3 dias</option>
                  <option value="d/4">d/4 - A cada 4 dias</option>
                  <option value="d/5">d/5 - A cada 5 dias</option>
                  <option value="d/7">d/7 - A cada 7 dias</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  ℹ️ Apenas informativo, não afeta a pontuação
                </p>
              </div>
            </div>

            {/* Seletor de Propriedade (para cálculo contextual 50/50) */}
            {properties.length > 0 && (
              <div className="mt-4">
                <label className="block font-medium text-gray-700 mb-2">
                  Propriedade do Trabalhador
                </label>
                <select
                  value={selectedProperty}
                  onChange={(e) => setSelectedProperty(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="property-select"
                >
                  <option value="">{t('evalSelectOption') || 'Selecione...'}</option>
                  {properties.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                
                {/* Indicador do modo de cálculo */}
                <div className={`mt-2 p-3 rounded-lg text-sm ${
                  selectedProperty && propertyHasContext 
                    ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                    : 'bg-blue-50 border border-blue-200 text-blue-800'
                }`} data-testid="calc-mode-indicator">
                  {selectedProperty && propertyHasContext ? (
                    <p><strong>50% Tecnico + 50% Contextual</strong> — Propriedade com contexto de producao configurado. O selo considerara a producao real vs meta.</p>
                  ) : selectedProperty ? (
                    <p><strong>100% Tecnico</strong> — Propriedade sem contexto de producao. Configure o contexto na aba Desempenho para habilitar o calculo 50/50.</p>
                  ) : (
                    <p><strong>100% Tecnico</strong> — Selecione uma propriedade para verificar se o calculo 50/50 esta disponivel.</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Grupos de Critérios */}
          {Object.entries(criteriaGroups).map(([groupKey, group]) => {
            const groupScore = calculateGroupScore(groupKey);
            const maxGroupScore = group.items.reduce((sum, item) => sum + item.max, 0);
            const percentage = Math.round((groupScore / maxGroupScore) * 100);
            const isExpanded = expandedGroups[groupKey];
            
            return (
              <div 
                key={groupKey} 
                className={`bg-white rounded-xl shadow-lg mb-4 overflow-hidden border-l-4 ${
                  group.color === 'blue' ? 'border-l-blue-500' :
                  group.color === 'green' ? 'border-l-green-500' :
                  group.color === 'amber' ? 'border-l-amber-500' :
                  'border-l-purple-500'
                }`}
              >
                {/* Header do Grupo (clicável) */}
                <button
                  type="button"
                  onClick={() => toggleGroup(groupKey)}
                  className={`w-full p-4 flex items-center justify-between ${group.bgColor} hover:opacity-90 transition-opacity`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`font-bold text-lg ${group.textColor}`}>
                      {t('evalGroup')} {groupKey}
                    </span>
                    <span className="text-gray-700 font-medium">
                      {group.title}
                    </span>
                    <span className="text-gray-500 text-sm">
                      ({group.subtitle})
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <span className={`font-bold text-lg ${group.textColor}`}>
                        {groupScore.toFixed(1)}/{maxGroupScore}
                      </span>
                      <span className="text-gray-500 text-sm ml-2">
                        ({percentage}%)
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                </button>
                
                {/* Conteúdo do Grupo (expansível) */}
                {isExpanded && (
                  <div className="p-4 space-y-4">
                    {group.items.map(criterion => (
                      <div 
                        key={criterion.key} 
                        className={`p-4 rounded-lg ${group.bgColor} ${group.borderColor} border`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <label className={`font-medium ${group.textColor}`}>
                              {criterion.label}
                            </label>
                            {ENABLE_TOOLTIPS && (
                              <EvaluationTooltip 
                                criterion={criterion.key} 
                                maxScore={criterion.max}
                                currentScore={criteria[criterion.key]}
                              />
                            )}
                          </div>
                          <span className={`font-bold text-lg ${group.textColor}`}>
                            {criteria[criterion.key]}/{criterion.max} {criterion.unit}
                          </span>
                        </div>
                        
                        <input
                          type="range"
                          min="0"
                          max={criterion.max}
                          step={criterion.step}
                          value={criteria[criterion.key]}
                          onChange={(e) => handleCriteriaChange(criterion.key, e.target.value, criterion.max)}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                        
                        <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                          <Info className="w-3 h-3" />
                          {criterion.hint}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Observações */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">📝 Observações</h2>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
              placeholder="Adicione observações sobre a avaliação (opcional)..."
            />
          </div>

          {/* Resumo e Pontuação */}
          <div className={`rounded-xl shadow-lg p-6 mb-6 ${
            seal.type === 'gold' ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' :
            seal.type === 'silver' ? 'bg-gradient-to-r from-gray-400 to-gray-600' :
            seal.type === 'bronze' ? 'bg-gradient-to-r from-amber-500 to-amber-700' :
            'bg-gradient-to-r from-gray-500 to-gray-700'
          } text-white`}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium opacity-90">{t('evalTotalScore')}</h2>
                <p className="text-5xl font-bold mt-1">{totalScore.toFixed(1)}/100</p>
              </div>
              <div className="text-right">
                <p className="text-6xl">{seal.emoji}</p>
                <p className="text-lg font-medium mt-2">{seal.label}</p>
              </div>
            </div>
            
            {/* Barra de progresso por grupo */}
            <div className="mt-6 grid grid-cols-4 gap-2">
              {Object.entries(criteriaGroups).map(([groupKey, group]) => {
                const groupScore = calculateGroupScore(groupKey);
                const maxGroupScore = group.items.reduce((sum, item) => sum + item.max, 0);
                const percentage = Math.round((groupScore / maxGroupScore) * 100);
                
                return (
                  <div key={groupKey} className="text-center">
                    <p className="text-xs opacity-80 mb-1">{t('evalGroup')} {groupKey}</p>
                    <div className="h-2 bg-white/30 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-white rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <p className="text-xs mt-1">{percentage}%</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Botão Submit */}
          <button
            type="submit"
            disabled={loading || success}
            className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all ${
              loading || success
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
            }`}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white" />
                Criando Avaliação...
              </>
            ) : success ? (
              <>✅ Avaliação Criada!</>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Criar Avaliação
              </>
            )}
          </button>
        </form>
      </div>
    </div>
    </>
  );
};

export default EvaluationForm;
